import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { useState } from "react";
import type { AccessSummary } from "@/features/access/accessTypes";
import { openBillingPortal } from "@/features/billing/billingApi";

type AccessSummaryCardProps = {
  access: AccessSummary | null;
  isLoading: boolean;
  error: string | null;
};

function formatTier(access: AccessSummary): string {
  if (access.source === "lifetime_standard") {
    return "Lifetime Standard";
  }

  const tier = access.effectiveTier;
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function formatAccessNote(access: AccessSummary): string {
  const usageNote = `${access.usage.remainingThisMonth} of ${access.usage.monthlyBlueprintLimit} monthly blueprint generations remain.`;

  if (access.source === "free") {
    return usageNote;
  }

  if (access.source === "lifetime_standard") {
    return `Standard access is lifetime and does not expire. ${usageNote}`;
  }

  if (access.source === "standard_window") {
    const accessNote = access.standard.daysRemaining === null
      ? "Standard access is active."
      : `Standard access has ${access.standard.daysRemaining} days remaining.`;

    return `${accessNote} ${usageNote}`;
  }

  if (access.source === "pro_trial") {
    return `Pro trial access is active. ${usageNote}`;
  }

  if (access.source === "pro_grace") {
    return `Pro grace-period access is active. ${usageNote}`;
  }

  return `Pro access is active. ${usageNote}`;
}

export function AccessSummaryCard({
  access,
  isLoading,
  error,
}: AccessSummaryCardProps) {
  const { getToken } = useAuth();
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [portalMessage, setPortalMessage] = useState<string | null>(null);
  const [hasNoBillingAccount, setHasNoBillingAccount] = useState(false);

  async function handleOpenBillingPortal() {
    if (hasNoBillingAccount) {
      return;
    }

    setIsPortalLoading(true);
    setPortalMessage(null);

    try {
      const token = await getToken();
      const response = await openBillingPortal(token);
      window.location.href = response.portalUrl;
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Billing management is unavailable right now.";
      setPortalMessage(message);
      if (message.toLowerCase().includes("no billing account")) {
        setHasNoBillingAccount(true);
      }
    } finally {
      setIsPortalLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <p className="text-sm text-slate-500">Effective access</p>
        <div className="mt-3 h-7 w-36 animate-pulse rounded bg-slate-100" />
        <div className="mt-3 h-4 w-full max-w-sm animate-pulse rounded bg-slate-100" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-5">
        <p className="text-sm font-semibold text-red-900">
          Access summary unavailable
        </p>
        <p className="mt-2 text-sm text-red-800">{error}</p>
      </div>
    );
  }

  if (!access) {
    return null;
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm text-slate-500">Your access</p>
          <p className="mt-2 text-xl font-semibold text-slate-950">
            {formatTier(access)}
          </p>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {formatAccessNote(access)}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {access.effectiveTier !== "pro" ? (
          <Link
            to="/pricing"
            className="inline-flex rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            View upgrade options
          </Link>
        ) : null}
        <button
          type="button"
          onClick={() => void handleOpenBillingPortal()}
          disabled={isPortalLoading || hasNoBillingAccount}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
        >
          {hasNoBillingAccount
            ? "No billing account"
            : isPortalLoading
              ? "Opening billing..."
              : "Manage billing"}
        </button>
      </div>
      {hasNoBillingAccount ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          No billing account is on file yet. Visit pricing to start checkout.
        </div>
      ) : portalMessage ? (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {portalMessage}
        </div>
      ) : null}
      <div className="mt-5 grid gap-3 text-sm sm:grid-cols-3">
        <div>
          <p className="font-semibold text-slate-800">Fast Mode</p>
          <p className="mt-1 text-slate-500">
            {access.capabilities.canUseFastBlueprint ? "Available" : "Locked"}
          </p>
        </div>
        <div>
          <p className="font-semibold text-slate-800">Guided Mode</p>
          <p className="mt-1 text-slate-500">
            {access.capabilities.canUseGuidedMode
              ? "Available"
              : "Included with Standard"}
          </p>
        </div>
        <div>
          <p className="font-semibold text-slate-800">Pro Tools</p>
          <p className="mt-1 text-slate-500">
            {access.capabilities.canUseProTools
              ? "Available"
              : "Included with Pro"}
          </p>
        </div>
      </div>
    </div>
  );
}
